import type { TransactionStatus, PaymentResultStatus, PaymentResult, PaymentMessage } from './types';

const SUCCESS_STATUSES: TransactionStatus[] = ['settlement', 'capture', 'success', 'succeeded'];
const PENDING_STATUSES: TransactionStatus[] = ['pending', 'challenge', 'processing'];
const ERROR_STATUSES: TransactionStatus[] = ['deny', 'cancel', 'expire', 'failure', 'expired', 'canceled', 'failed'];

export function mapTransactionStatus(status: TransactionStatus): PaymentResultStatus {
  if (SUCCESS_STATUSES.includes(status)) return 'success';
  if (PENDING_STATUSES.includes(status)) return 'pending';
  if (ERROR_STATUSES.includes(status)) return 'error';
  return 'error';
}

export function isValidPaymentMessage(data: unknown): data is PaymentMessage {
  if (typeof data !== 'object' || data === null) return false;
  const msg = data as Record<string, unknown>;
  return typeof msg.transaction_status === 'string';
}

/**
 * Builds a result from a status string sent by the payment page. A status this SDK does not know is
 * reported as an error rather than dropped, so the merchant's callback always fires.
 */
export function createPaymentResultFromStatus(
  transactionStatus: string,
  rawData: Record<string, unknown>
): PaymentResult {
  const status = transactionStatus as TransactionStatus;
  return {
    transactionStatus: status,
    status: mapTransactionStatus(status),
    rawData,
  };
}

export function createPaymentResult(message: PaymentMessage): PaymentResult {
  return createPaymentResultFromStatus(message.transaction_status, message);
}

let scrollLock: { overflow: string; paddingRight: string } | null = null;

/**
 * Stops the page behind the modal from scrolling. The scrollbar width is added back as
 * padding so the page does not jump sideways when the scrollbar disappears, and the
 * merchant's own inline values are restored on unlock instead of being cleared.
 */
export function lockBodyScroll(): void {
  if (scrollLock) return;

  const { body, documentElement } = document;
  scrollLock = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };

  const scrollbarWidth = window.innerWidth - documentElement.clientWidth;
  if (scrollbarWidth > 0) {
    const currentPadding = parseFloat(getComputedStyle(body).paddingRight) || 0;
    body.style.paddingRight = `${currentPadding + scrollbarWidth}px`;
  }
  body.style.overflow = 'hidden';
}

export function unlockBodyScroll(): void {
  if (!scrollLock) return;

  document.body.style.overflow = scrollLock.overflow;
  document.body.style.paddingRight = scrollLock.paddingRight;
  scrollLock = null;
}

/**
 * Copies text for the payment page (which cannot write to the clipboard from a cross-origin
 * frame on its own). Falls back to a hidden textarea where the async clipboard API is missing
 * or rejects, e.g. on a merchant page served over plain http.
 */
export function copyToClipboard(text: string): void {
  const fallback = () => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0;';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } catch {
      console.warn('[arto-pay/js-sdk] Could not copy to clipboard');
    }
    textarea.remove();
  };

  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(fallback);
    return;
  }
  fallback();
}
